import {
  QueuedFile,
  UploadQueueState,
  UploadQueueStats,
} from '@/types/uploadQueue';

/**
 * Generate unique ID for queued file
 */
export const generateFileId = (file: File): string => {
  return `${file.name}-${file.size}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};

/**
 * Calculate upload queue statistics
 */
export const calculateQueueStats = (files: QueuedFile[]): UploadQueueStats => {
  return files.reduce(
    (stats, file) => {
      stats.total++;
      stats[file.status]++;
      return stats;
    },
    {
      total: 0,
      pending: 0,
      uploading: 0,
      completed: 0,
      error: 0,
      cancelled: 0,
    }
  );
};

/**
 * Calculate total progress percentage
 */
export const calculateTotalProgress = (files: QueuedFile[]): number => {
  if (files.length === 0) return 0;

  const totalProgress = files.reduce((sum, file) => sum + file.progress, 0);
  return Math.round(totalProgress / files.length);
};

/**
 * Get next file to upload
 */
export const getNextFileToUpload = (files: QueuedFile[]): QueuedFile | null => {
  return files.find(file => file.status === 'pending') || null;
};

/**
 * Check if upload is complete
 */
export const isUploadComplete = (files: QueuedFile[]): boolean => {
  return (
    files.length > 0 &&
    files.every(
      file =>
        file.status === 'completed' ||
        file.status === 'error' ||
        file.status === 'cancelled'
    )
  );
};

/**
 * Get files with errors
 */
export const getErrorFiles = (files: QueuedFile[]): QueuedFile[] => {
  return files.filter(file => file.status === 'error');
};

/**
 * Format upload time remaining
 */
export const formatTimeRemaining = (
  startTime: Date,
  completedFiles: number,
  totalFiles: number
): string => {
  if (completedFiles === 0) return 'Calculating...';

  const elapsed = Date.now() - startTime.getTime();
  const avgTimePerFile = elapsed / completedFiles;
  const remainingFiles = totalFiles - completedFiles;
  const estimatedTimeRemaining = avgTimePerFile * remainingFiles;

  const minutes = Math.floor(estimatedTimeRemaining / 60000);
  const seconds = Math.floor((estimatedTimeRemaining % 60000) / 1000);

  if (minutes > 0) {
    return `${minutes}m ${seconds}s remaining`;
  }
  return `${seconds}s remaining`;
};

// LocalStorage keys
const UPLOAD_QUEUE_STORAGE_KEY = 'uploadQueue';
const UPLOAD_QUEUE_VERSION = '1.0';

/**
 * Save upload queue to localStorage.
 * Only saves completed/error files (pending/uploading cannot be restored without File objects).
 */
export const saveQueueToStorage = (state: UploadQueueState): void => {
  try {
    const serializedFiles = state.files
      .filter(
        file =>
          file.status === 'completed' || file.status === 'error'
      )
      .map(file => ({
        id: file.id,
        fileName: file.file.name,
        fileSize: file.file.size,
        fileType: file.file.type,
        status: file.status,
        progress: file.progress,
        error: file.error,
        uploadedAt: file.uploadedAt,
        addedAt: file.addedAt,
      }));

    const serializedState = {
      version: UPLOAD_QUEUE_VERSION,
      files: serializedFiles,
      isUploading: state.isUploading,
      currentUploadIndex: state.currentUploadIndex,
      totalProgress: state.totalProgress,
      errors: state.errors,
      isPaused: state.isPaused,
      savedAt: new Date().toISOString(),
    };

    localStorage.setItem(
      UPLOAD_QUEUE_STORAGE_KEY,
      JSON.stringify(serializedState)
    );
  } catch {
    // Best-effort persistence: localStorage may be unavailable (private mode, quota, etc.).
  }
};

/**
 * Load upload queue from localStorage
 */
export const loadQueueFromStorage = (): Partial<UploadQueueState> | null => {
  try {
    const stored = localStorage.getItem(UPLOAD_QUEUE_STORAGE_KEY);
    if (!stored) return null;

    const parsed = JSON.parse(stored);

    // Check version compatibility
    if (parsed.version !== UPLOAD_QUEUE_VERSION) {
      clearQueueStorage();
      return null;
    }

    // Only restore completed/error files metadata (can't restore File objects)
    const restoredFiles = parsed.files
      .filter(
        (file: { status: string }) =>
          file.status === 'completed' || file.status === 'error'
      )
      .map(
        (file: {
          id: string;
          fileName: string;
          fileSize: number;
          fileType: string;
          status: string;
          progress: number;
          error?: string;
          uploadedAt?: string;
          addedAt: string;
        }) => ({
          ...file,
          file: new File([], file.fileName, { type: file.fileType }), // Placeholder File object
          addedAt: new Date(file.addedAt),
          uploadedAt: file.uploadedAt ? new Date(file.uploadedAt) : undefined,
        })
      );

    return {
      files: restoredFiles,
      isUploading: false, // Never restore uploading state
      currentUploadIndex: 0,
      totalProgress: parsed.totalProgress,
      errors: parsed.errors.map(
        (error: {
          fileId: string;
          fileName: string;
          error: string;
          timestamp: string;
        }) => ({
          ...error,
          timestamp: new Date(error.timestamp),
        })
      ),
      isPaused: false,
    };
  } catch {
    clearQueueStorage();
    return null;
  }
};

/**
 * Clear upload queue from localStorage
 */
export const clearQueueStorage = (): void => {
  try {
    localStorage.removeItem(UPLOAD_QUEUE_STORAGE_KEY);
  } catch {
    // Best-effort: ignore localStorage failures.
  }
};

/**
 * Debounced save to localStorage
 */
let saveTimeout: NodeJS.Timeout | null = null;

export const debouncedSaveToStorage = (
  state: UploadQueueState,
  delay: number = 1000
): void => {
  if (saveTimeout) {
    clearTimeout(saveTimeout);
  }

  saveTimeout = setTimeout(() => {
    saveQueueToStorage(state);
    saveTimeout = null;
  }, delay);
};
